import React, { useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useDivision } from '@/context/DivisionContext';
import { Plus, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const { getAuthHeaders } = useAuth();
  const { division, getCountyLabel } = useDivision();
  const [loading, setLoading] = useState(false);
  
  const [bidData, setBidData] = useState({
    county: '',
    highway: '',
    control_number: '',
    letting_date: '',
    unit_type: 'LF',
    material_type: 'asphalt', // New: asphalt, concrete, both
    // Asphalt Edgeline
    edgeline_lf: '',
    edgeline_price_mo: '',
    edgeline_price_tc: '',
    // Asphalt Centerline
    centerline_lf: '',
    centerline_price_mo: '',
    centerline_price_tc: '',
    // Concrete Edgeline
    concrete_edgeline_lf: '',
    concrete_edgeline_price_mo: '',
    concrete_edgeline_price_tc: '',
    // Concrete Centerline
    concrete_centerline_lf: '',
    concrete_centerline_price_mo: '',
    concrete_centerline_price_tc: '',
    // Day rate
    day_total_mo: '',
    day_total_tc: '',
    status: 'pending'
  });

  // Calculate totals automatically based on unit type and material type
  const edgeline_lf = parseFloat(bidData.edgeline_lf) || 0;
  const edgeline_price_mo = parseFloat(bidData.edgeline_price_mo) || 0;
  const edgeline_price_tc = parseFloat(bidData.edgeline_price_tc) || 0;
  
  const centerline_lf = parseFloat(bidData.centerline_lf) || 0;
  const centerline_price_mo = parseFloat(bidData.centerline_price_mo) || 0;
  const centerline_price_tc = parseFloat(bidData.centerline_price_tc) || 0;
  
  const concrete_edgeline_lf = parseFloat(bidData.concrete_edgeline_lf) || 0;
  const concrete_edgeline_price_mo = parseFloat(bidData.concrete_edgeline_price_mo) || 0;
  const concrete_edgeline_price_tc = parseFloat(bidData.concrete_edgeline_price_tc) || 0;
  
  const concrete_centerline_lf = parseFloat(bidData.concrete_centerline_lf) || 0;
  const concrete_centerline_price_mo = parseFloat(bidData.concrete_centerline_price_mo) || 0;
  const concrete_centerline_price_tc = parseFloat(bidData.concrete_centerline_price_tc) || 0;
  
  const edgeline_total_mo = edgeline_lf * edgeline_price_mo;
  const edgeline_total_tc = edgeline_lf * edgeline_price_tc;
  const centerline_total_mo = centerline_lf * centerline_price_mo;
  const centerline_total_tc = centerline_lf * centerline_price_tc;
  
  const concrete_edgeline_total_mo = concrete_edgeline_lf * concrete_edgeline_price_mo;
  const concrete_edgeline_total_tc = concrete_edgeline_lf * concrete_edgeline_price_tc;
  const concrete_centerline_total_mo = concrete_centerline_lf * concrete_centerline_price_mo;
  const concrete_centerline_total_tc = concrete_centerline_lf * concrete_centerline_price_tc;
  
  let total_milling_only, total_with_tc, total_lf;
  
  if (bidData.unit_type === 'DAY') {
    // Day rate - use direct input
    total_milling_only = parseFloat(bidData.day_total_mo) || 0;
    total_with_tc = parseFloat(bidData.day_total_tc) || 0;
    total_lf = 0;
  } else {
    // Linear feet - calculate from all sections based on material type
    total_milling_only = edgeline_total_mo + centerline_total_mo + concrete_edgeline_total_mo + concrete_centerline_total_mo;
    total_with_tc = edgeline_total_tc + centerline_total_tc + concrete_edgeline_total_tc + concrete_centerline_total_tc;
    total_lf = edgeline_lf + centerline_lf + concrete_edgeline_lf + concrete_centerline_lf;
  }

  const handleChange = (field, value) => {
    setBidData({ ...bidData, [field]: value });
  };

  const resetForm = () => {
    setBidData({
      county: '',
      highway: '',
      control_number: '',
      letting_date: '',
      unit_type: 'LF',
      material_type: 'asphalt',
      edgeline_lf: '',
      edgeline_price_mo: '',
      edgeline_price_tc: '',
      centerline_lf: '',
      centerline_price_mo: '',
      centerline_price_tc: '',
      concrete_edgeline_lf: '',
      concrete_edgeline_price_mo: '',
      concrete_edgeline_price_tc: '',
      concrete_centerline_lf: '',
      concrete_centerline_price_mo: '',
      concrete_centerline_price_tc: '',
      day_total_mo: '',
      day_total_tc: '',
      status: 'pending'
    });
  };

  const saveBid = async () => {
    // Validate required fields
    if (!bidData.county || !bidData.highway || !bidData.control_number) {
      toast.error("Please fill in County, Highway, and Control Number");
      return;
    }

    if (total_milling_only === 0) {
      toast.error("Please enter footage and pricing information");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/add-bid`, {
        county: bidData.county.toUpperCase(),
        highway: bidData.highway.toUpperCase(),
        control_number: bidData.control_number.toUpperCase(),
        letting_date: bidData.letting_date || new Date().toISOString().split('T')[0],
        unit_type: bidData.unit_type,
        bid_qty: total_lf,
        milling_only_total: total_milling_only,
        tc_total: total_with_tc,
        status: bidData.status,
        division: division
      }, {
        headers: getAuthHeaders()
      });

      toast.success("Bid added successfully!");
      resetForm();
    } catch (error) {
      console.error('Error adding bid:', error);
      toast.error(error.response?.data?.detail || 'Failed to add bid');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f172a] to-[#1e293b] p-8 ml-64">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-2">
            <span className="text-[#f97316]">Admin</span> Data Entry
          </h1>
          <p className="text-[#94a3b8] text-sm uppercase tracking-widest">
            Manual Bid Entry for Historical Data
          </p>
        </div>

        <Card className="millmetrix-card p-8">
          {/* Unit Type and Material Type - At the top */}
          <div className="mb-8 p-4 bg-[#1e293b] rounded-lg border-2 border-[#f97316]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Job Type */}
              <div>
                <Label htmlFor="unit_type" className="text-xs font-bold uppercase tracking-widest text-[#f97316]">
                  Job Type *
                </Label>
                <select
                  id="unit_type"
                  value={bidData.unit_type}
                  onChange={(e) => handleChange('unit_type', e.target.value)}
                  className="millmetrix-input mt-2 w-full bg-[#0f172a] border-[#334155] text-white text-lg font-semibold"
                >
                  <option value="LF">Linear Feet (Edgeline + Centerline)</option>
                  <option value="DAY">Day Rate Job</option>
                </select>
              </div>

              {/* Material Type - Only show for LF jobs */}
              {bidData.unit_type === 'LF' && (
                <div>
                  <Label htmlFor="material_type" className="text-xs font-bold uppercase tracking-widest text-[#f97316]">
                    Material Type *
                  </Label>
                  <select
                    id="material_type"
                    value={bidData.material_type}
                    onChange={(e) => handleChange('material_type', e.target.value)}
                    className="millmetrix-input mt-2 w-full bg-[#0f172a] border-[#334155] text-white text-lg font-semibold"
                  >
                    <option value="asphalt">Asphalt (Default)</option>
                    <option value="concrete">Concrete</option>
                    <option value="both">Both (Asphalt + Concrete)</option>
                  </select>
                </div>
              )}
            </div>
            <p className="text-xs text-[#94a3b8] mt-3">
              {bidData.unit_type === 'LF' 
                ? `Material: ${bidData.material_type === 'asphalt' ? 'Asphalt only' : bidData.material_type === 'concrete' ? 'Concrete only' : 'Both asphalt and concrete'}`
                : 'Enter total day rate prices only (no footage required)'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* County/Parish */}
            <div>
              <Label htmlFor="county" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                {getCountyLabel()} *
              </Label>
              <Input
                id="county"
                type="text"
                value={bidData.county}
                onChange={(e) => handleChange('county', e.target.value.toUpperCase())}
                className="millmetrix-input uppercase mt-2"
                placeholder="e.g., BELL"
              />
            </div>

            {/* Highway */}
            <div>
              <Label htmlFor="highway" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Highway/Roadway *
              </Label>
              <Input
                id="highway"
                type="text"
                value={bidData.highway}
                onChange={(e) => handleChange('highway', e.target.value.toUpperCase())}
                className="millmetrix-input uppercase mt-2"
                placeholder="e.g., US 190"
              />
            </div>

            {/* Control Number */}
            <div>
              <Label htmlFor="control_number" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Control Number *
              </Label>
              <Input
                id="control_number"
                type="text"
                value={bidData.control_number}
                onChange={(e) => handleChange('control_number', e.target.value.toUpperCase())}
                className="millmetrix-input uppercase mt-2"
                placeholder="e.g., 6479-53-001"
              />
            </div>

            {/* Letting Date */}
            <div>
              <Label htmlFor="letting_date" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Letting Date
              </Label>
              <Input
                id="letting_date"
                type="date"
                value={bidData.letting_date}
                onChange={(e) => handleChange('letting_date', e.target.value)}
                className="millmetrix-input mt-2"
              />
            </div>

            {/* Status */}
            <div>
              <Label htmlFor="status" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Status
              </Label>
              <select
                id="status"
                value={bidData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className="millmetrix-input mt-2 w-full bg-[#0f172a] border-[#1e293b] text-white"
              >
                <option value="pending">Pending (Open)</option>
                <option value="won">Won (Completed)</option>
                <option value="lost">Lost</option>
              </select>
            </div>
          </div>

          {/* DAY RATE INPUTS - Show only if unit_type is DAY */}
          {bidData.unit_type === 'DAY' && (
            <div className="mt-8">
              <h3 className="text-xl font-bold text-[#f97316] mb-4 uppercase tracking-wide">Day Rate Pricing</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Milling Only Day Rate */}
                <div>
                  <Label htmlFor="day_total_mo" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Milling Only (Day Rate) *
                  </Label>
                  <Input
                    id="day_total_mo"
                    type="number"
                    step="0.01"
                    value={bidData.day_total_mo}
                    onChange={(e) => handleChange('day_total_mo', e.target.value)}
                    className="millmetrix-input mt-2"
                    placeholder="e.g., 5000.00"
                  />
                </div>

                {/* With TC&C Day Rate */}
                <div>
                  <Label htmlFor="day_total_tc" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    With TC&C (Day Rate)
                  </Label>
                  <Input
                    id="day_total_tc"
                    type="number"
                    step="0.01"
                    value={bidData.day_total_tc}
                    onChange={(e) => handleChange('day_total_tc', e.target.value)}
                    className="millmetrix-input mt-2"
                    placeholder="e.g., 9500.00"
                  />
                </div>
              </div>

              {/* Day Rate Totals Display */}
              {total_milling_only > 0 && (
                <div className="mt-6 p-6 bg-gradient-to-r from-[#1e293b] to-[#0f172a] rounded-lg border-2 border-[#f97316]">
                  <h3 className="text-lg font-bold text-white mb-4 uppercase tracking-wide">Day Rate Totals</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="text-center">
                      <div className="text-xs text-[#94a3b8] uppercase tracking-wider mb-1">Milling Only</div>
                      <div className="text-2xl font-bold text-[#0ea5e9]">${total_milling_only.toFixed(2)}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-[#94a3b8] uppercase tracking-wider mb-1">With TC&C</div>
                      <div className="text-2xl font-bold text-[#f97316]">${total_with_tc.toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* LINEAR FEET INPUTS - Show only if unit_type is LF */}
          {bidData.unit_type === 'LF' && (
            <>
              {/* ASPHALT SECTIONS - Show for asphalt or both */}
              {(bidData.material_type === 'asphalt' || bidData.material_type === 'both') && (
                <>
                  {/* ASPHALT EDGELINE SECTION */}
                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-[#f97316] mb-4 uppercase tracking-wide">
                      {bidData.material_type === 'both' ? 'Asphalt Edgeline' : 'Edgeline'}
                    </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Edgeline Linear Feet */}
              <div>
                <Label htmlFor="edgeline_lf" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Linear Feet
                </Label>
                <Input
                  id="edgeline_lf"
                  type="number"
                  value={bidData.edgeline_lf}
                  onChange={(e) => handleChange('edgeline_lf', e.target.value)}
                  className="millmetrix-input mt-2"
                  placeholder="e.g., 50000"
                />
              </div>

              {/* Edgeline Price/LF (Milling Only) */}
              <div>
                <Label htmlFor="edgeline_price_mo" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Price/LF (Milling Only)
                </Label>
                <Input
                  id="edgeline_price_mo"
                  type="number"
                  step="0.01"
                  value={bidData.edgeline_price_mo}
                  onChange={(e) => handleChange('edgeline_price_mo', e.target.value)}
                  className="millmetrix-input mt-2"
                  placeholder="e.g., 0.20"
                />
              </div>

              {/* Edgeline Price/LF (With TC&C) */}
              <div>
                <Label htmlFor="edgeline_price_tc" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Price/LF (With TC&C)
                </Label>
                <Input
                  id="edgeline_price_tc"
                  type="number"
                  step="0.01"
                  value={bidData.edgeline_price_tc}
                  onChange={(e) => handleChange('edgeline_price_tc', e.target.value)}
                  className="millmetrix-input mt-2"
                  placeholder="e.g., 0.38"
                />
              </div>
            </div>

            {/* Edgeline Totals */}
            {edgeline_lf > 0 && (
              <div className="mt-3 p-3 bg-[#1e293b] rounded-md border border-[#334155]">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-[#94a3b8]">Edgeline M.O. Total:</span>
                    <span className="text-[#0ea5e9] font-bold ml-2">${edgeline_total_mo.toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-[#94a3b8]">Edgeline TC&C Total:</span>
                    <span className="text-[#f97316] font-bold ml-2">${edgeline_total_tc.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

                  {/* ASPHALT CENTERLINE SECTION */}
                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-[#f97316] mb-4 uppercase tracking-wide">
                      {bidData.material_type === 'both' ? 'Asphalt Centerline' : 'Centerline'}
                    </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Centerline Linear Feet */}
              <div>
                <Label htmlFor="centerline_lf" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Linear Feet
                </Label>
                <Input
                  id="centerline_lf"
                  type="number"
                  value={bidData.centerline_lf}
                  onChange={(e) => handleChange('centerline_lf', e.target.value)}
                  className="millmetrix-input mt-2"
                  placeholder="e.g., 25000"
                />
              </div>

              {/* Centerline Price/LF (Milling Only) */}
              <div>
                <Label htmlFor="centerline_price_mo" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Price/LF (Milling Only)
                </Label>
                <Input
                  id="centerline_price_mo"
                  type="number"
                  step="0.01"
                  value={bidData.centerline_price_mo}
                  onChange={(e) => handleChange('centerline_price_mo', e.target.value)}
                  className="millmetrix-input mt-2"
                  placeholder="e.g., 0.20"
                />
              </div>

              {/* Centerline Price/LF (With TC&C) */}
              <div>
                <Label htmlFor="centerline_price_tc" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Price/LF (With TC&C)
                </Label>
                <Input
                  id="centerline_price_tc"
                  type="number"
                  step="0.01"
                  value={bidData.centerline_price_tc}
                  onChange={(e) => handleChange('centerline_price_tc', e.target.value)}
                  className="millmetrix-input mt-2"
                  placeholder="e.g., 0.38"
                />
              </div>
            </div>

            {/* Centerline Totals */}
            {centerline_lf > 0 && (
              <div className="mt-3 p-3 bg-[#1e293b] rounded-md border border-[#334155]">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-[#94a3b8]">Centerline M.O. Total:</span>
                    <span className="text-[#0ea5e9] font-bold ml-2">${centerline_total_mo.toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-[#94a3b8]">Centerline TC&C Total:</span>
                    <span className="text-[#f97316] font-bold ml-2">${centerline_total_tc.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
                  </div>
                </>
              )}

              {/* CONCRETE SECTIONS - Show for concrete or both */}
              {(bidData.material_type === 'concrete' || bidData.material_type === 'both') && (
                <>
                  {/* CONCRETE EDGELINE SECTION */}
                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-[#10b981] mb-4 uppercase tracking-wide">
                      {bidData.material_type === 'both' ? 'Concrete Edgeline' : 'Edgeline'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Concrete Edgeline Linear Feet */}
                      <div>
                        <Label htmlFor="concrete_edgeline_lf" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                          Linear Feet
                        </Label>
                        <Input
                          id="concrete_edgeline_lf"
                          type="number"
                          value={bidData.concrete_edgeline_lf}
                          onChange={(e) => handleChange('concrete_edgeline_lf', e.target.value)}
                          className="millmetrix-input mt-2"
                          placeholder="e.g., 50000"
                        />
                      </div>

                      {/* Concrete Edgeline Price/LF (Milling Only) */}
                      <div>
                        <Label htmlFor="concrete_edgeline_price_mo" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                          Price/LF (Milling Only)
                        </Label>
                        <Input
                          id="concrete_edgeline_price_mo"
                          type="number"
                          step="0.01"
                          value={bidData.concrete_edgeline_price_mo}
                          onChange={(e) => handleChange('concrete_edgeline_price_mo', e.target.value)}
                          className="millmetrix-input mt-2"
                          placeholder="e.g., 1.20"
                        />
                      </div>

                      {/* Concrete Edgeline Price/LF (With TC&C) */}
                      <div>
                        <Label htmlFor="concrete_edgeline_price_tc" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                          Price/LF (With TC&C)
                        </Label>
                        <Input
                          id="concrete_edgeline_price_tc"
                          type="number"
                          step="0.01"
                          value={bidData.concrete_edgeline_price_tc}
                          onChange={(e) => handleChange('concrete_edgeline_price_tc', e.target.value)}
                          className="millmetrix-input mt-2"
                          placeholder="e.g., 1.38"
                        />
                      </div>
                    </div>

                    {/* Concrete Edgeline Totals */}
                    {concrete_edgeline_lf > 0 && (
                      <div className="mt-3 p-3 bg-[#1e293b] rounded-md border border-[#10b981]">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-[#94a3b8]">Concrete Edgeline M.O. Total:</span>
                            <span className="text-[#0ea5e9] font-bold ml-2">${concrete_edgeline_total_mo.toFixed(2)}</span>
                          </div>
                          <div>
                            <span className="text-[#94a3b8]">Concrete Edgeline TC&C Total:</span>
                            <span className="text-[#10b981] font-bold ml-2">${concrete_edgeline_total_tc.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* CONCRETE CENTERLINE SECTION */}
                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-[#10b981] mb-4 uppercase tracking-wide">
                      {bidData.material_type === 'both' ? 'Concrete Centerline' : 'Centerline'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Concrete Centerline Linear Feet */}
                      <div>
                        <Label htmlFor="concrete_centerline_lf" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                          Linear Feet
                        </Label>
                        <Input
                          id="concrete_centerline_lf"
                          type="number"
                          value={bidData.concrete_centerline_lf}
                          onChange={(e) => handleChange('concrete_centerline_lf', e.target.value)}
                          className="millmetrix-input mt-2"
                          placeholder="e.g., 25000"
                        />
                      </div>

                      {/* Concrete Centerline Price/LF (Milling Only) */}
                      <div>
                        <Label htmlFor="concrete_centerline_price_mo" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                          Price/LF (Milling Only)
                        </Label>
                        <Input
                          id="concrete_centerline_price_mo"
                          type="number"
                          step="0.01"
                          value={bidData.concrete_centerline_price_mo}
                          onChange={(e) => handleChange('concrete_centerline_price_mo', e.target.value)}
                          className="millmetrix-input mt-2"
                          placeholder="e.g., 1.20"
                        />
                      </div>

                      {/* Concrete Centerline Price/LF (With TC&C) */}
                      <div>
                        <Label htmlFor="concrete_centerline_price_tc" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                          Price/LF (With TC&C)
                        </Label>
                        <Input
                          id="concrete_centerline_price_tc"
                          type="number"
                          step="0.01"
                          value={bidData.concrete_centerline_price_tc}
                          onChange={(e) => handleChange('concrete_centerline_price_tc', e.target.value)}
                          className="millmetrix-input mt-2"
                          placeholder="e.g., 1.38"
                        />
                      </div>
                    </div>

                    {/* Concrete Centerline Totals */}
                    {concrete_centerline_lf > 0 && (
                      <div className="mt-3 p-3 bg-[#1e293b] rounded-md border border-[#10b981]">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-[#94a3b8]">Concrete Centerline M.O. Total:</span>
                            <span className="text-[#0ea5e9] font-bold ml-2">${concrete_centerline_total_mo.toFixed(2)}</span>
                          </div>
                          <div>
                            <span className="text-[#94a3b8]">Concrete Centerline TC&C Total:</span>
                            <span className="text-[#10b981] font-bold ml-2">${concrete_centerline_total_tc.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* GRAND TOTALS */}
              {total_lf > 0 && (
                <div className="mt-8 p-6 bg-gradient-to-r from-[#1e293b] to-[#0f172a] rounded-lg border-2 border-[#f97316]">
                  <h3 className="text-lg font-bold text-white mb-4 uppercase tracking-wide">Bid Totals</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="text-xs text-[#94a3b8] uppercase tracking-wider mb-1">Total Linear Feet</div>
                      <div className="text-2xl font-bold text-white">{total_lf.toLocaleString()}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-[#94a3b8] uppercase tracking-wider mb-1">Milling Only Total</div>
                      <div className="text-2xl font-bold text-[#0ea5e9]">${total_milling_only.toFixed(2)}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-[#94a3b8] uppercase tracking-wider mb-1">With TC&C Total</div>
                      <div className="text-2xl font-bold text-[#f97316]">${total_with_tc.toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4 mt-8">
            <Button
              onClick={saveBid}
              disabled={loading}
              className="millmetrix-button bg-[#f97316] hover:bg-[#ea580c] text-white flex-1"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : 'Save Bid'}
            </Button>
            
            <Button
              onClick={resetForm}
              variant="outline"
              className="border-[#334155] text-[#94a3b8] hover:bg-[#1e293b] hover:text-white"
            >
              Clear Form
            </Button>
          </div>

          <div className="mt-6 p-4 bg-[#1e293b] rounded-lg border border-[#334155]">
            <p className="text-xs text-[#94a3b8]">
              <strong className="text-[#f97316]">Note:</strong> This form is for quick entry of historical bid data. 
              These bids won't have detailed margin analysis. Use the Bidding Calculator for new projects that require full cost analysis.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Admin;
